// src/components/organisms/Home/DisplayMountain.tsx

import { useState, useEffect, ReactElement, useRef, useContext } from 'react';
import styled from 'styled-components';
import { HIKER, MOUNTAIN, ROAD, VICTORY_HIKER } from '../../../constants/images';
import { ReactComponent as RoadSVG } from '../../../constants/road2.svg';
import { motion, useAnimation } from 'framer-motion';
import { landscapeData } from './Mountain/LandscapeData';
import { hikerSteps } from './Mountain/HikerData';
import {
  calculateResponsiveHikerPosition,
  setHikerParams,
  animateHikerToNewStep
} from './Mountain/hikerLogic';
import { animatedLandscape, findElementsByValue } from './Mountain/landscapeLogic';
import { UserGamificationDetails } from '../../../types/welcome';
import { useResonanceLayout } from '../ResonnanceLayout/ResoncanceContext';
import MilestoneModal from '../../../pages/Profile/MilestoneModal';
import { useAuth } from '../../../contexts/AuthContext';
import NotificationConsentModal from '../../../pages/Profile/NotificationConsentModal';
import { patchNotificationConsent } from '../../../services/user.service';
import { useConfig } from '../../../contexts/ConfigContext';

const background = { src: MOUNTAIN };
const victoryHiker = { src: VICTORY_HIKER };

// === Milestone configuration ===
const milestoneMap = {
  ms1: { id: 1, startOffset: 0, duration: 5 },
  ms2: { id: 2, startOffset: 15, duration: 7 },
  ms3: { id: 3, startOffset: 30, duration: 7 },
  ms4: { id: 4, startOffset: 60, duration: 15 },
  ms5: { id: 5, startOffset: 90, duration: 15 },
  ms6: { id: 6, startOffset: 180, duration: 15 },
  ms7: { id: 7, startOffset: 270, duration: 15 },
  ms8: { id: 8, startOffset: 365, duration: 15 }
};

const CUTOFF_UTC_ISO = '2025-08-17T22:00:00Z';
function parseDate(v?: string | number | null) {
  if (v == null) return null;
  if (typeof v === 'number') return new Date(v < 2_000_000_000 ? v * 1000 : v);
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

const DisplayMountain = ({
  onAnimationComplete,
  disableHeaderFinished
}: {
  onAnimationComplete: (done: boolean) => void;
  disableHeaderFinished: boolean;
}): ReactElement => {
  const { setResonanceLayout, userGamificationDetails, questionnaires } = useResonanceLayout();

   const [modalOpen, setModalOpen] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
 const { user, getCurrent } = useAuth();
  const { study } = useConfig();

  const [consentOpen, setConsentOpen] = useState(false);
  const [consentSaving, setConsentSaving] = useState(false);
  const consentShownOnceRef = useRef(false);

  // ⬇️ ADD
const shouldShowConsent = (() => {
  const createdAt = parseDate(user?.attributes?.created_at);
  const unanswered = user?.attributes?.notifications_opt_in == null;
  if (!createdAt || !unanswered) return false;
  return createdAt.getTime() < new Date(CUTOFF_UTC_ISO).getTime();
})();

useEffect(() => {
  if (shouldShowConsent && !consentShownOnceRef.current) {
    setConsentOpen(true);
    consentShownOnceRef.current = true;
  }
}, [shouldShowConsent]);


const answerConsent = async (optIn: boolean) => {
  setConsentSaving(true);
  try {
    await patchNotificationConsent(study.id, optIn); // ✅ study.id from context
    // Refetch the current user so state is canonical across tabs/reloads
    await getCurrent();
    setConsentOpen(false);
  } finally {
    setConsentSaving(false);
  }
};


  const openMilestone = (id: string) => {
    const milestone = milestoneMap[id];
    if (!milestone || !user?.attributes.created_at) return;
    setSelectedMilestone(milestone);
    setModalOpen(true);
  };

  const [parentWidth, setParentWidth] = useState(0);
  const [ratio, setRatio] = useState({ width: 1, height: 1 });

  const [initialBackgroundSize] = useState({ width: 390, height: 844 });
  const [initialPosition] = useState({ x: 320, y: 720 });
  const initialHikerSize = { width: 100, height: 180 };

  const [backgroundSize, setBackgroundSize] = useState({ ...initialBackgroundSize });
  const [hikerSize, sethikerSize] = useState({ ...initialHikerSize });
  const [hikerScale, setHikerScale] = useState(1);

  const [currentResponsivePosition, setCurrentResponsivePosition] = useState({ ...initialPosition });

  function getInitialAnimationStatus() {
    return {
      hikerAnimationStep: 0,
      landscapeAnimationPlayed: [false, false, false, false, false, false, false, false],
    };
  }

  const [currentAnimationStatus, setCurrentAnimationStatus] = useState(getInitialAnimationStatus());
  const [newAnimationStatus, setNewAnimationStatus] = useState(getInitialAnimationStatus());

  const [shouldFlip, setShouldFlip] = useState(false);

  const [handleResizeFinished, setHandleResizeFinished] = useState(false);
  const [hikerSizeFinished, setHikerSizeFinished] = useState(false);
  const [updateAnimationStatusFinished, setUpdateAnimationStatusFinished] = useState(false);
  const [currentResponsivePositionFinished, setCurrentResponsivePositionFinished] = useState(false);

  const controls = useAnimation();

  const [hikerAnimationInProgress, setHikerAnimationInProgress] = useState(true);
  const [landscapeAnimationInProgress, setLandscapeAnimationInProgress] = useState(true);

  const landscapeBackgroundRef = useRef<HTMLImageElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);

  // NEW: ref to the inlined SVG so we can grab its <circle> elements
  const roadRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
  if (!roadRef.current || !user?.attributes?.created_at) return;

  const svg = roadRef.current;
  const handlers: Array<{ el: Element; handler: EventListener }> = [];

  // Add glow CSS
  const style = document.createElement("style");
  style.innerHTML = glowCSS;
  document.head.appendChild(style);

  const inclusionDate = new Date(user.attributes.created_at);
  const today = new Date();
  const diffDays = Math.floor((today.getTime() - inclusionDate.getTime()) / (1000 * 60 * 60 * 24));
  console.log("📊 diffDays since inclusion:", diffDays);

  let nextMilestoneElement: SVGCircleElement | null = null;

  for (let i = 1; i <= 8; i++) {
    const id = `ms${i}`;
    const el = svg.getElementById(id);
    const milestone = milestoneMap[id];

    if (el && milestone) {
      const { startOffset } = milestone;

      console.log(`🔁 ${id}: starts at day ${startOffset}`);

      const handler = () => openMilestone(id);
      el.addEventListener("click", handler);
      el.setAttribute("style", "cursor: pointer; pointer-events: auto;");
      handlers.push({ el, handler });

      // Select the first future milestone (and stop at the first one)
      if (!nextMilestoneElement && diffDays < startOffset) {
        nextMilestoneElement = el as SVGCircleElement;
      }
    }
  }

  if (nextMilestoneElement) {
    const { cx, cy, r } = nextMilestoneElement;
    const glow = document.createElementNS("http://www.w3.org/2000/svg", "circle");
    glow.setAttribute("cx", cx.baseVal.value.toString());
    glow.setAttribute("cy", cy.baseVal.value.toString());
    glow.setAttribute("r", r.baseVal.value.toString());
    glow.setAttribute("class", "milestone-glow");
    nextMilestoneElement.parentElement?.insertBefore(glow, nextMilestoneElement);
    console.log("✨ Highlighting next upcoming milestone only.");
  }

  return () => {
    handlers.forEach(({ el, handler }) => el.removeEventListener("click", handler));
    document.head.removeChild(style);
  };
}, [user]);




  /**
   * Define BackgroundDimension using landscapeRef
   * Define ratio to the initial background size
   * Define parent width using parentRef
   */
  useEffect(() => {
    const handleResize = () => {
      if (
        landscapeBackgroundRef.current &&
        parentRef.current &&
        disableHeaderFinished
      ) {
        if (
          backgroundSize.height !== 0 &&
          backgroundSize.width !== 0 &&
          parentWidth !== 0
        ) {
          setHandleResizeFinished(true);
        } else {
          const { width, height } =
            landscapeBackgroundRef.current.getBoundingClientRect();

          if (width === 0 && height === 0) {
            setBackgroundSize({ width: 0, height: 0 });
          } else {
            setBackgroundSize({ width, height });
            setRatio({
              width: width / initialBackgroundSize.width,
              height: height / initialBackgroundSize.height,
            });
          }

          if (parentRef.current.offsetWidth === 0) {
            setParentWidth(0);
          } else {
            setParentWidth(parentRef.current.offsetWidth);
          }
        }
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [
    parentRef,
    landscapeBackgroundRef,
    backgroundSize,
    parentWidth,
    disableHeaderFinished,
  ]);

  /**
   * Define NewHikerSize according to ratio of background
   */
  useEffect(() => {
    sethikerSize({
      width: initialHikerSize.width * ratio.width,
      height: initialHikerSize.height * ratio.height,
    });

    setHikerSizeFinished(true);
  }, [ratio]);

  /**
   * Update data in HikerLogic
   * Calculate responsive hiker position
   */
  useEffect(() => {
    if (updateAnimationStatusFinished) {
      setHikerParams({
        ratio,
        parentWidth,
        backgroundSize,
        hikerSize,
      });

      const position = calculateResponsiveHikerPosition(
        hikerSteps[currentAnimationStatus.hikerAnimationStep].position,
        hikerSteps[currentAnimationStatus.hikerAnimationStep].flipOnLoading,
        hikerSteps[currentAnimationStatus.hikerAnimationStep].scale
      );
      setCurrentResponsivePosition({
        x: position.x,
        y: position.y,
      });

      setCurrentResponsivePositionFinished(true);
    }
  }, [updateAnimationStatusFinished]);

  /**
   * Animate hiker from current step to new step
   */
  useEffect(() => {
    if (currentResponsivePositionFinished) {
      if (
        newAnimationStatus.hikerAnimationStep !==
          currentAnimationStatus.hikerAnimationStep &&
        hikerSteps[currentAnimationStatus.hikerAnimationStep].flipOnLoading ===
          shouldFlip
      ) {
        const newScale =
          hikerSteps[newAnimationStatus.hikerAnimationStep].scale;

        const animate = async () => {
          setHikerAnimationInProgress(true);
          try {
            await animateHikerToNewStep(
              controls,
              currentAnimationStatus.hikerAnimationStep,
              newAnimationStatus.hikerAnimationStep,
              newScale
            );
          } catch (error) {
            console.error(error);
          }
          setHikerAnimationInProgress(false);
        };
        animate();
      } else {
        setHikerAnimationInProgress(false);
      }
    }
  }, [currentResponsivePositionFinished, shouldFlip]);

  function UpdateAnimationStatus() {
    let bufferCurrent = getInitialAnimationStatus();
    let bufferNew = getInitialAnimationStatus();

    const animationSuccess = userGamificationDetails.animation_success;
    const animationSuccessEarned =
      userGamificationDetails.animation_successEarned;
    const step = userGamificationDetails.step;
    const stepEarned = userGamificationDetails.stepEarned;

    if (
      userGamificationDetails.pointsEarned === 0 &&
      userGamificationDetails.stepEarned === 0 &&
      userGamificationDetails.animation_successEarned === 0
    ) {
      const animationValues = findElementsByValue(animationSuccess);

      animationValues.forEach((value) => {
        const matches = landscapeData.filter(
          (ld) => ld.keyword.toLowerCase() === value.toLowerCase()
        );
        matches.forEach((match) => {
          const idx = landscapeData.indexOf(match);
          bufferCurrent.landscapeAnimationPlayed[idx] =
            !bufferCurrent.landscapeAnimationPlayed[idx];
        });
      });

      bufferCurrent.hikerAnimationStep = step;
      setCurrentAnimationStatus(bufferCurrent);
      setNewAnimationStatus(bufferCurrent);
    } else {
      const currentValues = findElementsByValue(
        animationSuccess - animationSuccessEarned
      );
      currentValues.forEach((value) => {
        const matches = landscapeData.filter(
          (ld) => ld.keyword.toLowerCase() === value.toLowerCase()
        );
        matches.forEach((match) => {
          const idx = landscapeData.indexOf(match);
          bufferCurrent.landscapeAnimationPlayed[idx] =
            !bufferCurrent.landscapeAnimationPlayed[idx];
        });
      });
      bufferCurrent.hikerAnimationStep = step - stepEarned;
      setCurrentAnimationStatus(bufferCurrent);

      const newValues = findElementsByValue(animationSuccess);
      newValues.forEach((value) => {
        const matches = landscapeData.filter(
          (ld) => ld.keyword.toLowerCase() === value.toLowerCase()
        );
        matches.forEach((match) => {
          const idx = landscapeData.indexOf(match);
          bufferNew.landscapeAnimationPlayed[idx] =
            !bufferNew.landscapeAnimationPlayed[idx];
        });
      });
      bufferNew.hikerAnimationStep = step;
      setNewAnimationStatus(bufferNew);
    }

    const flip = hikerSteps[step - stepEarned].flipOnLoading;
    setShouldFlip(flip);
  }

  useEffect(() => {
    try {
      if (
        userGamificationDetails &&
        handleResizeFinished &&
        hikerSizeFinished
      ) {
        UpdateAnimationStatus();
        setHikerScale(hikerSteps[userGamificationDetails.step].scale);
        setUpdateAnimationStatusFinished(true);
      }
    } catch (error) {
      console.error('Error updating animation status:', error);
    }
  }, [userGamificationDetails, handleResizeFinished, hikerSizeFinished]);

  useEffect(() => {
    return () => {
      if (
        userGamificationDetails.pointsEarned !== 0 ||
        userGamificationDetails.stepEarned !== 0 ||
        userGamificationDetails.animation_successEarned !== 0
      ) {
        const updates: UserGamificationDetails = {
          points: userGamificationDetails.points,
          step: userGamificationDetails.step,
          animation_success: userGamificationDetails.animation_success,
          pointsEarned: 0,
          stepEarned: 0,
          animation_successEarned: 0,
        };
        setResonanceLayout({ userGamificationDetails: updates });
      }
    };
  }, []);

  const landscapeAnimationStart = () => {
    setLandscapeAnimationInProgress(true);
  };

  const landscapeAnimationComplete = () => {
    setLandscapeAnimationInProgress(false);
  };

  useEffect(() => {
    if (!hikerAnimationInProgress && !landscapeAnimationInProgress)
      onAnimationComplete(true);
    else onAnimationComplete(false);
  }, [hikerAnimationInProgress, landscapeAnimationInProgress]);

  return (
    <div ref={parentRef}>
      {/* mountain background */}
      <LandscapeImageFixed
        src={background.src}
        ref={landscapeBackgroundRef}
      />

      {/* landscape elements */}
      {currentResponsivePositionFinished &&
        landscapeData.map(({ id, src }) => (
          <motion.div
            key={id}
            initial={{
              opacity: landscapeData[id - 1].animation.startOpacity,
            }}
            animate={animatedLandscape(
              id,
              currentAnimationStatus.landscapeAnimationPlayed,
              newAnimationStatus.landscapeAnimationPlayed
            )}
            onAnimationStart={() => {
              if (
                currentAnimationStatus.landscapeAnimationPlayed[
                  id - 1
                ] !==
                newAnimationStatus.landscapeAnimationPlayed[
                  id - 1
                ]
              ) {
                landscapeAnimationStart();
              }
            }}
            onAnimationComplete={() => {
              if (
                currentAnimationStatus.landscapeAnimationPlayed[
                  id - 1
                ] !==
                newAnimationStatus.landscapeAnimationPlayed[
                  id - 1
                ]
              ) {
                landscapeAnimationComplete();
              }
            }}
          >
            <LandscapeImage src={src} />
          </motion.div>
        ))}

      {/* ——— REPLACE original road.png with: ——— */}
      {/* <LandscapeImageFixed src={road.src} /> */}
      <RoadWrapper>
        <RoadSVG
          ref={roadRef}
          preserveAspectRatio="xMidYMin meet"
          style={{ pointerEvents: 'none' }}
        />
      </RoadWrapper>

      {/* hiker or victory */}
      {newAnimationStatus.hikerAnimationStep === 8 &&
      !hikerAnimationInProgress ? (
        <LandscapeImageFixed src={victoryHiker.src} />
      ) : (
        <Hiker
          src={HIKER}
          alt="Hiker"
          animate={controls}
          style={{
            left: `${currentResponsivePosition.x}px`,
            top: `${currentResponsivePosition.y}px`,
            width: `${hikerSize.width}px`,
            height: `${hikerSize.height}px`,
            transform: `scale(${hikerScale}) ${
              shouldFlip ? 'scaleX(-1)' : 'scaleX(1)'
            }`,
          }}
        />
      )}
      <NotificationConsentModal
  isOpen={consentOpen}
  saving={consentSaving}
  onDismiss={() => setConsentOpen(false)}
  onAccept={() => answerConsent(true)}
  onDecline={() => answerConsent(false)}
/>
       {selectedMilestone && user?.attributes.created_at && (
        <MilestoneModal
          isOpen={modalOpen}
          onDismiss={() => setModalOpen(false)}
          milestone={selectedMilestone}
          inclusionDate={new Date(user.attributes.created_at)}
          user={user.attributes}
          onAccessQuestionnaire={() => {
            setModalOpen(false);
            window.location.href = `/mentalo/resonance/sequences`; // adjust if needed
          }}
        />
      )}
    </div>
  );
};

export default DisplayMountain;

// ─── Styled Components ───────────────────────────────────

const Hiker = styled(motion.img)`
  position: absolute;
`;

const LandscapeImage = styled.img`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const LandscapeImageFixed = styled.img`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

// NEW: wrapper for inline SVG road
const RoadWrapper = styled.div`
     position: absolute;
    top: 0px;
    left: 50%;
    transform: translateX(-50%);
    height: 100%;
    width: auto;
    max-width: 100%;
    aspect-ratio: 780 / 1688;
    pointer-events: none;
    object-fit: contain;
    max-height: 100%;
`;
const glowCSS = `
@keyframes glowing {
  0% { filter: drop-shadow(0 0 4px #aafaff); }
  50% { filter: drop-shadow(0 0 10px #ffffff); }
  100% { filter: drop-shadow(0 0 4px #aafaff); }
}

.milestone-glow {
  fill: #ffffff;
  stroke: #ffffff;
  stroke-width: 2;
  opacity: 0.8;
  animation: glowing 2s infinite ease-in-out;
  pointer-events: none;
}
`;


